import { useState, useEffect, useRef, useCallback } from 'react';
import { AlphaTabApi } from '@coderline/alphatab';
import * as Tone from 'tone';

export interface UseMetronomeOptions {
  alphaTabApi: AlphaTabApi | null;
  enabled: boolean;
  muted: boolean;
  /** Current playing tempo in BPM (user-adjusted) */
  currentTempo: number;
}

export interface UseMetronomeReturn {
  isBeatActive: boolean;
  isDownbeatActive: boolean;
  currentBeat: number;
  beatsPerMeasure: number;
  scoreTempo: number;
}

const LED_PULSE_DURATION_MS = 100;
const CLICK_SAMPLE_URL = '/sounds/metronome-click.mp3';

export function useMetronome(
  options: UseMetronomeOptions
): UseMetronomeReturn {
  const { alphaTabApi, enabled, muted, currentTempo } = options;

  const [isBeatActive, setIsBeatActive] = useState(false);
  const [isDownbeatActive, setIsDownbeatActive] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [scoreTempo, setScoreTempo] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);

  const beatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const downbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const metronomeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const beatCountRef = useRef<number>(0); // Absolute beat count since playback started
  const downbeatPlayerRef = useRef<Tone.Player | null>(null);
  const beatPlayerRef = useRef<Tone.Player | null>(null);
  const beatsPerMeasureRef = useRef<number>(4);

  const clearTimeouts = useCallback(() => {
    if (beatTimeoutRef.current) {
      clearTimeout(beatTimeoutRef.current);
      beatTimeoutRef.current = null;
    }
    if (downbeatTimeoutRef.current) {
      clearTimeout(downbeatTimeoutRef.current);
      downbeatTimeoutRef.current = null;
    }
  }, []);

  // Initialize Tone.js players with metronome sample
  useEffect(() => {
    // Downbeat player - slightly higher pitch for emphasis
    downbeatPlayerRef.current = new Tone.Player(CLICK_SAMPLE_URL).toDestination();
    downbeatPlayerRef.current.playbackRate = 1.2; // Higher pitch for downbeat

    // Regular beat player - normal pitch
    beatPlayerRef.current = new Tone.Player(CLICK_SAMPLE_URL).toDestination();

    return () => {
      downbeatPlayerRef.current?.dispose();
      beatPlayerRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!alphaTabApi) {
      return;
    }

    const handleScoreLoaded = (score: {
      tempo: number;
      masterBars: Array<{ timeSignatureNumerator: number }>;
    }) => {
      if (score.tempo > 0) {
        setScoreTempo(score.tempo);
      }
      if (score.masterBars?.[0]?.timeSignatureNumerator > 0) {
        setBeatsPerMeasure(score.masterBars[0].timeSignatureNumerator);
        beatsPerMeasureRef.current = score.masterBars[0].timeSignatureNumerator;
      }
      beatCountRef.current = 0;
    };

    alphaTabApi.scoreLoaded.on(handleScoreLoaded);

    return () => {
      alphaTabApi.scoreLoaded.off(handleScoreLoaded);
    };
  }, [alphaTabApi]);

  // Track playback state
  useEffect(() => {
    if (!alphaTabApi) return;

    const handlePlayerStateChanged = (args: { state: number }) => {
      // State 0 = stopped/paused, 1 = playing
      if (args.state === 1) {
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
        // Reset beat count on stop/pause
        beatCountRef.current = 0;
        setCurrentBeat(0);
      }
    };

    alphaTabApi.playerStateChanged.on(handlePlayerStateChanged);

    return () => {
      alphaTabApi.playerStateChanged.off(handlePlayerStateChanged);
    };
  }, [alphaTabApi]);

  // Clear metronome interval
  const clearMetronomeInterval = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
  }, []);

  // Trigger a single beat click and LED flash
  const triggerBeat = useCallback(() => {
    const beatsPerMeasure = beatsPerMeasureRef.current;
    const beatInMeasure = (beatCountRef.current % beatsPerMeasure) + 1;
    const isDownbeat = beatInMeasure === 1;

    setCurrentBeat(beatInMeasure);
    beatCountRef.current += 1;

    // Clear existing LED timeouts
    clearTimeouts();

    // Activate beat LED
    setIsBeatActive(true);
    beatTimeoutRef.current = setTimeout(() => {
      setIsBeatActive(false);
    }, LED_PULSE_DURATION_MS);

    // Activate downbeat LED on beat 1
    if (isDownbeat) {
      setIsDownbeatActive(true);
      downbeatTimeoutRef.current = setTimeout(() => {
        setIsDownbeatActive(false);
      }, LED_PULSE_DURATION_MS);
    }

    // Trigger audio click (if not muted)
    if (!muted) {
      if (isDownbeat && downbeatPlayerRef.current?.loaded) {
        downbeatPlayerRef.current.start();
      } else if (!isDownbeat && beatPlayerRef.current?.loaded) {
        beatPlayerRef.current.start();
      }
    }
  }, [muted, clearTimeouts]);

  // Timer-based metronome - runs at currentTempo rate
  useEffect(() => {
    // Clear any existing interval
    clearMetronomeInterval();

    // Only run if playing and enabled
    if (!isPlaying || !enabled) {
      return;
    }

    // Guard against zero or negative tempo
    if (currentTempo <= 0) return;

    // Calculate interval in ms from BPM
    const intervalMs = 60000 / currentTempo;

    // Trigger first beat immediately
    triggerBeat();

    // Start interval for subsequent beats
    metronomeIntervalRef.current = setInterval(() => {
      triggerBeat();
    }, intervalMs);

    return () => {
      clearMetronomeInterval();
    };
  }, [isPlaying, enabled, currentTempo, triggerBeat, clearMetronomeInterval]);

  return {
    isBeatActive,
    isDownbeatActive,
    currentBeat,
    beatsPerMeasure,
    scoreTempo,
  };
}
